import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Row, Col } from "react-bootstrap";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import { toast } from "react-toastify";
import ApiService from "../../../functions/apiService";
import ButtonTheme from "../../../components/ThemeButton/ThemeButton";

export default function AssignmentHistoryModal({ show, onHide, empId, empName, onChanged }) {
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [rows, setRows] = useState([]);
    const [confirmRow, setConfirmRow] = useState(null);

    const loadHistory = () => {
        if (!empId) return;
        setLoading(true);
        ApiService.get("/getEmployeeAssignmentHistory/" + empId)
            .then(({ data }) => {
                setRows(data?.data || []);
            })
            .catch((err) => {
                toast.error(err?.data?.message || "Unable to load assignment history");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (show) {
            setConfirmRow(null);
            loadHistory();
        }
    }, [show, empId]);

    const handleDelete = () => {
        if (!confirmRow) return;
        setDeleting(true);
        ApiService.delete("/deleteEmployeeAssignmentRecord", {
            Emp_Code: confirmRow.Emp_Code,
            Effective_Date: confirmRow.Effective_Date,
        })
            .then((res) => {
                toast.success(res?.message || "Assignment deleted");
                setConfirmRow(null);
                loadHistory();
                onChanged && onChanged();
            })
            .catch((err) => {
                toast.error(err?.data?.message || "Unable to delete this assignment");
            })
            .finally(() => {
                setDeleting(false);
            });
    };

    const formatDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : "-");

    const rowLabel = (row) => {
        if (row.is_current) return <span className="badge bg-success">Current</span>;
        if (row.is_future) return <span className="badge bg-warning text-dark">Upcoming</span>;
        return <span className="badge bg-secondary">Past</span>;
    };

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Assignment History{empName ? ` - ${empName}` : ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <Box className="d-flex justify-content-center p-5">
                            <CircularProgress color="secondary" />
                        </Box>
                    ) : rows.length === 0 ? (
                        <div style={{ color: "#555555" }} className="p-3 text-center">
                            No assignment records found for this member.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Effective Date</th>
                                        <th></th>
                                        <th>Status</th>
                                        <th>Rank</th>
                                        <th>Group</th>
                                        <th>Assignment</th>
                                        <th>Position</th>
                                        <th className="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            style={
                                                row.is_current
                                                    ? { backgroundColor: "#FDF2F3" }
                                                    : row.is_future
                                                        ? { color: "#777777" }
                                                        : undefined
                                            }
                                        >
                                            <td className={row.is_current ? "fw-500" : ""}>
                                                {formatDate(row.Effective_Date)}
                                            </td>
                                            <td>{rowLabel(row)}</td>
                                            <td>{row.StatusName || row.EA_Status || "-"}</td>
                                            <td>{row.RankName || row.EA_Rank || "-"}</td>
                                            <td>{row.GroupName || row.Group_Code || "-"}</td>
                                            <td>{row.AssignmentName || row.Assignment_Code || "-"}</td>
                                            <td>{row.PositionName || row.Position_Code || "-"}</td>
                                            <td className="text-end">
                                                <Tooltip title="Delete assignment">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setConfirmRow(row)}
                                                    >
                                                        <DeleteOutlineIcon
                                                            fontSize="small"
                                                            style={{ color: "#BF1D2D" }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ButtonTheme type="button" variant="filled" onClick={onHide}>
                        Close
                    </ButtonTheme>
                </Modal.Footer>
            </Modal>

            <Modal show={!!confirmRow} onHide={() => setConfirmRow(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Deletion Warning</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ color: "#555555" }}>
                        You are about to delete the assignment effective{" "}
                        <b>{formatDate(confirmRow?.Effective_Date)}</b>. This cannot be undone.
                    </div>
                    {confirmRow?.is_current && (
                        <div style={{ color: "#BF1D2D" }} className="mt-2">
                            This is the member's active assignment. Deleting it will fall back to
                            the previous assignment record.
                        </div>
                    )}
                    <Row>
                        <Col md="auto">
                            <ButtonTheme
                                type="button"
                                variant="filled"
                                color="DarkRed"
                                className="mt-4"
                                disabled={deleting}
                                onClick={handleDelete}
                            >
                                {deleting ? "Deleting..." : "Confirm"}
                            </ButtonTheme>
                        </Col>
                        <Col md="auto">
                            <ButtonTheme
                                type="button"
                                variant="filled"
                                className="mt-4"
                                onClick={() => setConfirmRow(null)}
                            >
                                Cancel
                            </ButtonTheme>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
        </>
    );
}
